// Blend-mode options and helpers for the Layers panel dropdown.
// The layer-panel rendering itself lives in the canonical inline
// renderLayersPanel() in main.ts (#211).
import type { BlendMode, Layer } from "../core/document/Layer";

// ---------------------------------------------------------------------------
// Layer-tree flattening (i28)
// ---------------------------------------------------------------------------
//
// renderLayersPanel() in main.ts previously built rows straight from the
// top-level `doc.layers` array, so anything moved into a GroupLayer's
// `children` vanished from the panel entirely. The fix needs a recursive
// walk of the layer tree that:
//   - orders rows top-first per level (mirrors the existing
//     `[...doc.layers].reverse()` convention for the root list),
//   - skips a group's children when the group is collapsed,
//   - reports enough position info (parentId + bottomIndex) for the
//     drag-reorder wiring in main.ts to target the correct list/position
//     via `ReorderLayerCommand`, which already supports arbitrary
//     `targetParentId` + bottom-first `targetIndex`.
//
// Pulled out into this DOM-free module (rather than living inline in
// main.ts) purely so it's unit-testable without importing all of main.ts.

export interface LayerRowEntry {
  readonly layer: Layer;
  /** Nesting depth: 0 for top-level layers, 1 for a group's direct children, etc. */
  readonly depth: number;
  /** The containing group's id, or null for top-level layers. */
  readonly parentId: string | null;
  /**
   * This layer's index within its immediate parent's own layers array,
   * bottom-first — i.e. the same convention as `Document.layers` /
   * `GroupLayer.children` and the `targetIndex` param of
   * `ReorderLayerCommand`.
   */
  readonly bottomIndex: number;
}

/**
 * Flatten a layer tree into an ordered list of display rows, top-first
 * (matches the Layers panel convention: newest/topmost layer at the top).
 * Descends into a GroupLayer's `children` unless that group's id is present
 * in `collapsedIds`. Only "group" layers are treated as containers — this
 * mirrors the recursion convention already used by `findLayer` / `findRaster`
 * (src/core/document/LayerUtils.ts), which likewise only descend into
 * `kind === "group"`.
 */
export function flattenLayerRows(
  layers: readonly Layer[],
  collapsedIds: ReadonlySet<string> = new Set(),
  depth = 0,
  parentId: string | null = null,
): LayerRowEntry[] {
  const rows: LayerRowEntry[] = [];
  for (let i = layers.length - 1; i >= 0; i--) {
    const layer = layers[i]!;
    rows.push({ layer, depth, parentId, bottomIndex: i });
    if (layer.kind === "group" && !collapsedIds.has(layer.id)) {
      rows.push(...flattenLayerRows(layer.children, collapsedIds, depth + 1, layer.id));
    }
  }
  return rows;
}

// Blend-mode options for the Layers panel dropdown (closes #131).
// Empty value = separator (rendered as a disabled divider).
//
// Kept here (not in main.ts) so the dropdown's rendering + selected-display
// logic can be unit-tested in isolation.
export const BLEND_OPTIONS: ReadonlyArray<readonly [BlendMode | "", string]> = [
  ["normal", "Normal"],
  ["pass", "Pass Through"],
  ["dissolve", "Dissolve"],
  ["", "—"],
  ["darken", "Darken"],
  ["multiply", "Multiply"],
  ["color-burn", "Color Burn"],
  ["linear-burn", "Linear Burn"],
  ["darker-color", "Darker Color"],
  ["", "—"],
  ["lighten", "Lighten"],
  ["screen", "Screen"],
  ["color-dodge", "Color Dodge"],
  ["linear-dodge", "Linear Dodge (Add)"],
  ["lighter-color", "Lighter Color"],
  ["", "—"],
  ["overlay", "Overlay"],
  ["soft-light", "Soft Light"],
  ["hard-light", "Hard Light"],
  ["vivid-light", "Vivid Light"],
  ["linear-light", "Linear Light"],
  ["pin-light", "Pin Light"],
  ["hard-mix", "Hard Mix"],
  ["", "—"],
  ["difference", "Difference"],
  ["exclusion", "Exclusion"],
  ["subtract", "Subtract"],
  ["divide", "Divide"],
  ["", "—"],
  ["hue", "Hue"],
  ["saturation", "Saturation"],
  ["color", "Color"],
  ["luminosity", "Luminosity"],
];

/**
 * Find the human-readable label for a blend mode value (e.g. "normal" → "Normal").
 * Used by the blend-mode dropdown's selected-display text.
 */
export function blendModeLabel(mode: BlendMode | string): string {
  for (const [v, label] of BLEND_OPTIONS) {
    if (v === mode) return label;
  }
  return "Normal";
}

/**
 * Populate `<select class="blend-select">` with the full BLEND_OPTIONS list
 * and pre-select the currently-active layer's blend mode (or "normal" if no
 * layer is active). Closes #131: the dropdown previously didn't pre-select
 * any option, so DOM inspection showed all option text concatenated. The
 * fix is to always have exactly one `<option selected>`, which is what the
 * native `<select>` displays in its collapsed state.
 *
 * `layerKind` restricts "Pass Through" to group layers (i210) — Photoshop
 * only ever offers Pass Through on a group's blend-mode dropdown; regular
 * (raster/shape/type/etc.) layers never show it. Pass `"group"` to include
 * it; any other value (or omitting the argument entirely, e.g. when no
 * layer is active) excludes it.
 */
export function populateBlendSelect(
  select: HTMLSelectElement,
  currentMode: BlendMode | string = "normal",
  layerKind?: string,
): void {
  const allowPassThrough = layerKind === "group";
  const options = allowPassThrough ? BLEND_OPTIONS : BLEND_OPTIONS.filter(([v]) => v !== "pass");
  // Build options with explicit `selected` on the matching value.
  let foundMatch = false;
  select.innerHTML = options.map(([v, label]) => {
    if (v === "") return `<option disabled>──────────</option>`;
    const sel = v === currentMode ? " selected" : "";
    if (v === currentMode) foundMatch = true;
    return `<option value="${v}"${sel}>${escapeHtml(label)}</option>`;
  }).join("");
  // Fallback: if currentMode wasn't in the list — either genuinely unknown,
  // or "pass" was filtered out for a non-group layer — ensure "normal" is
  // selected rather than silently leaving a mismatched/stale blend mode.
  if (!foundMatch) select.value = "normal";
  else select.value = currentMode;
}

/**
 * Return the text that the native `<select>` shows in its collapsed state
 * (i.e. the currently-selected option's text). This is what users see;
 * `select.textContent` returns ALL option text concatenated, which is
 * what triggered issue #131 during DOM inspection.
 */
export function blendSelectDisplayText(select: HTMLSelectElement): string {
  const opt = select.selectedOptions[0];
  if (opt && !opt.disabled) return opt.text;
  // Fallback: find the selected by value
  const byValue = Array.from(select.options).find((o) => o.value === select.value && !o.disabled);
  return byValue?.text ?? "Normal";
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c));
}
